// Este componente pinta un mapa con las tiendas cercanas dentro de un radio.
// Incluye botón "Usar mi ubicación" y lista lateral.
import React, { useEffect, useState, useCallback, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "../leaflet-fix";
import {
    Box, Typography, Grid, Card, CardContent, Stack,
    CircularProgress, Button,
    IconButton, Divider, useTheme, Paper,
    Slider, Chip, List, ListItemText, ListItemButton
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import StorefrontIcon from "@mui/icons-material/Storefront";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import SearchIcon from "@mui/icons-material/Search";
import NavigationIcon from "@mui/icons-material/Navigation";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PhoneIcon from "@mui/icons-material/Phone";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { motion, AnimatePresence } from "framer-motion";

import { getNearbyStores, syncStoresFromOSM } from "../api/stores";

const MotionBox = motion(Box);
const MotionCard = motion(Card);

function ResizeMap() {
    const map = useMap();
    useEffect(() => {
        setTimeout(() => {
            map.invalidateSize();
        }, 500);
    }, [map]);
    return null;
}

function FlyTo({ center, zoom }) {
    const map = useMap();
    useEffect(() => {
        if (center) map.flyTo(center, zoom, { duration: 0.7 });
    }, [center, zoom, map]);
    return null;
}

export default function MapaTiendas() {
    const theme = useTheme();
    const [lat, setLat] = useState(28.68);
    const [lon, setLon] = useState(-17.77);
    const [hasGeo, setHasGeo] = useState(false);
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(false);
    const [radius, setRadius] = useState(10);
    const [maxRadiusSynced, setMaxRadiusSynced] = useState(0);
    const [brand, setBrand] = useState("all");
    const [selectedStore, setSelectedStore] = useState(null);
    const [debouncedRadius, setDebouncedRadius] = useState(radius);
    const loadingRef = useRef(false);

    // Debounce para el radio
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedRadius(radius);
        }, 800);
        return () => clearTimeout(timer);
    }, [radius]);

    const lastSyncRef = useRef({ lat: 0, lon: 0, radius: 0, time: 0 });

    const cargarTiendas = useCallback(async (currentLat, currentLon, texto = "", forzarSync = false, currentRadius = debouncedRadius, currentBrand = brand) => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        setLoading(true);
        try {
            // 1. Cargar lo que ya tenemos en local de forma inmediata
            const params = { radius_km: currentRadius, limit: 100 };
            if (currentLat && currentLon) {
                params.lat = currentLat;
                params.lon = currentLon;
            }
            if (texto) params.q = texto;
            if (currentBrand !== "all") params.brand = currentBrand;

            const { data: localData } = await getNearbyStores(params);
            setStores(localData || []);

            // 2. Decidir si sincronizar con OSM en segundo plano
            const now = Date.now();
            const distDesdeLastSync = Math.sqrt(Math.pow(currentLat - lastSyncRef.current.lat, 2) + Math.pow(currentLon - lastSyncRef.current.lon, 2));
            // Sincroniza si: 
            // - Se fuerza
            // - O no hay datos locales
            // - O el radio es mayor al anterior sincronizado Y ha pasado tiempo/distancia
            const shouldSync = forzarSync || 
                             (localData && localData.length === 0) || 
                             (currentRadius > lastSyncRef.current.radius && (now - lastSyncRef.current.time > 60000 || distDesdeLastSync > 0.05));

            if (shouldSync && currentLat && currentLon) {
                console.log(`Sincronizando con OSM en segundo plano para radio ${currentRadius}km...`);
                // No usamos await aquí para no bloquear la UI si ya tenemos algunos datos
                syncStoresFromOSM({ lat: currentLat, lon: currentLon, radius_km: currentRadius })
                    .then(() => {
                        lastSyncRef.current = { lat: currentLat, lon: currentLon, radius: currentRadius, time: Date.now() };
                        // Recargar solo si no ha habido otra búsqueda en medio
                        return getNearbyStores(params);
                    })
                    .then(({ data: freshData }) => {
                        setStores(freshData || []);
                    })
                    .catch(syncErr => {
                        console.error("Background sync failed", syncErr);
                    });
            }
        } catch (err) {
            console.error("Error al cargar tiendas", err);
        } finally {
            setLoading(false);
            loadingRef.current = false;
        }
    }, [debouncedRadius, brand]);

    const pedirGeo = () => {
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const newLat = pos.coords.latitude;
                const newLon = pos.coords.longitude;
                setLat(newLat);
                setLon(newLon);
                setHasGeo(true);
                setMaxRadiusSynced(0);
                // Al pedir ubicación explícitamente, fuerza sync para asegurar que el área esté poblada
                cargarTiendas(newLat, newLon, "", true);
            },
            (err) => alert("No se pudo obtener ubicación: " + err.message),
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    useEffect(() => {
        cargarTiendas(lat, lon, "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedRadius, brand, lat, lon]);

    const center = [lat, lon];

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Header y Controles */}
            <MotionBox
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'center', justifyContent: 'space-between' }}
            >
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 2 }}>
                        <StorefrontIcon sx={{ fontSize: 40 }} /> Localizar Tienda
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7, fontWeight: 500 }}>
                        Encuentra los supermercados más cercanos a tu ubicación.
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" justifyContent="center">
                    <Paper sx={{ p: 0.5, display: 'flex', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.04)' }} elevation={0}>
                        {[
                            { id: 'all', label: 'Todos', color: 'primary' },
                            { id: 'hiperdino', label: 'HiperDino', color: 'success' },
                            { id: 'mercadona', label: 'Mercadona', color: 'warning' }
                        ].map((b) => (
                            <Button
                                key={b.id}
                                size="small"
                                onClick={() => { setBrand(b.id); setMaxRadiusSynced(0); }}
                                sx={{
                                    borderRadius: 1.5,
                                    px: 2,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    bgcolor: brand === b.id ? 'white' : 'transparent',
                                    color: brand === b.id ? `${b.color}.main` : 'text.secondary',
                                    boxShadow: brand === b.id ? 1 : 0,
                                    '&:hover': { bgcolor: brand === b.id ? 'white' : 'rgba(0,0,0,0.05)' }
                                }}
                            >
                                {b.label}
                            </Button>
                        ))}
                    </Paper>

                    <Box sx={{ width: 150, px: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>Radio: {radius} km</Typography>
                        <Slider
                            size="small"
                            value={radius}
                            min={1}
                            max={25}
                            onChange={(e, val) => setRadius(val)}
                            valueLabelDisplay="auto"
                        />
                    </Box>

                    <Button
                        variant="contained"
                        startIcon={<MyLocationIcon />}
                        onClick={pedirGeo}
                        sx={{ borderRadius: 3, fontWeight: 700, textTransform: 'none', px: 3 }}
                    >
                        Usar mi ubicación
                    </Button>
                </Stack>
            </MotionBox>

            {/* Cuerpo Principal */}
            <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                {/* Sidebar Resultados */}
                <Grid size={{ xs: 12, md: 3 }} sx={{ height: '100%' }}>
                    <Card sx={{ height: '100%', borderRadius: 4, display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'rgba(0,0,0,0.01)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Resultados</Typography>
                            <Chip label={`${stores.length} tiendas`} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
                        </Box>

                        <List sx={{ flex: 1, overflowY: 'auto', p: 0 }}>
                            {loading ? (
                                <Box sx={{ p: 4, textAlign: 'center' }}>
                                    <CircularProgress size={30} />
                                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.6 }}>Buscando...</Typography>
                                </Box>
                            ) : stores.length === 0 ? (
                                <Box sx={{ p: 4, textAlign: 'center', opacity: 0.5 }}>
                                    <SearchIcon sx={{ fontSize: 48, mb: 1 }} />
                                    <Typography variant="body2">No hay tiendas en esta zona</Typography>
                                </Box>
                            ) : (
                                stores.map(s => (
                                    <ListItemButton
                                        key={s.id}
                                        selected={selectedStore?.id === s.id}
                                        onClick={() => { setLat(s.lat); setLon(s.lon); setSelectedStore(s); }}
                                        sx={{
                                            borderBottom: '1px solid',
                                            borderColor: 'rgba(0,0,0,0.05)',
                                            '&.Mui-selected': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                borderLeft: '4px solid',
                                                borderColor: 'primary.main'
                                            }
                                        }}
                                    >
                                        <ListItemText
                                            primary={<Typography sx={{ fontWeight: 800, color: 'primary.main', fontSize: '0.9rem' }}>{s.nombre}</Typography>}
                                            secondary={
                                                <Box>
                                                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.7 }}>{s.tipo || 'General'}</Typography>
                                                    {s.distancia_km != null && (
                                                        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 800 }}>A {s.distancia_km.toFixed(2)} km</Typography>
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItemButton>
                                ))
                            )}
                        </List>
                    </Card>
                </Grid>

                {/* Mapa */}
                <Grid size={{ xs: 12, md: 9 }} sx={{ height: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                    <Paper sx={{ flex: 1, borderRadius: 4, overflow: 'hidden', position: 'relative', border: '1px solid', borderColor: 'divider', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
                        <MapContainer center={center} zoom={13} style={{ height: "100%", width: "100%" }}>
                            <TileLayer
                                attribution='&copy; OpenStreetMap'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <ResizeMap />
                            <FlyTo center={center} zoom={14} />

                            {hasGeo && (
                                <Marker position={center}>
                                    <Popup>Tu ubicación actual</Popup>
                                </Marker>
                            )}

                            {stores.map(s => s.lat && s.lon && (
                                <Marker key={s.id} position={[s.lat, s.lon]} eventHandlers={{ click: () => setSelectedStore(s) }}>
                                    <Popup>
                                        <Box sx={{ p: 1, minWidth: 180 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.main', borderBottom: '1px solid', borderColor: 'divider', pb: 0.5, mb: 1 }}>{s.nombre}</Typography>
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 1 }}>{s.tipo}</Typography>
                                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                                <LocationOnIcon sx={{ fontSize: 14, color: 'text.secondary', mt: 0.2 }} />
                                                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{s.direccion}</Typography>
                                            </Stack>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                                                {s.distancia_km != null && <Typography variant="caption" sx={{ fontWeight: 800, color: 'success.main' }}>{s.distancia_km.toFixed(2)} km</Typography>}
                                                <Button size="small" variant="contained" sx={{ fontSize: '0.6rem', px: 1, py: 0 }} onClick={() => setSelectedStore(s)}>Detalles</Button>
                                            </Box>
                                        </Box>
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>

                        {/* Card Detalle Flotante */}
                        <AnimatePresence>
                            {selectedStore && (
                                <MotionCard
                                    initial={{ opacity: 0, y: 100 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 100 }}
                                    sx={{
                                        position: 'absolute',
                                        bottom: 20,
                                        left: { xs: 20, md: 40 },
                                        right: { xs: 20, md: 'auto' },
                                        width: { md: 350 },
                                        zIndex: 1000,
                                        borderRadius: 4,
                                        boxShadow: '0 12px 48px rgba(0,0,0,0.2)',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <Box sx={{ p: 3, position: 'relative', background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, color: 'white' }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => setSelectedStore(null)}
                                            sx={{ position: 'absolute', top: 10, right: 10, color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                        <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>{selectedStore.nombre}</Typography>
                                        <Chip label={selectedStore.tipo} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 800, fontSize: '0.65rem' }} />
                                    </Box>

                                    <CardContent sx={{ p: 3 }}>
                                        <Stack spacing={2}>
                                            {selectedStore.direccion && (
                                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                                    <LocationOnIcon color="action" />
                                                    <Typography variant="body2">{selectedStore.direccion}</Typography>
                                                </Stack>
                                            )}
                                            {selectedStore.horarioabierto && (
                                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                                    <AccessTimeIcon color="success" />
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 800, color: 'success.main' }}>Abierto</Typography>
                                                        <Typography variant="caption" color="text.secondary">{selectedStore.horarioabierto}</Typography>
                                                    </Box>
                                                </Stack>
                                            )}
                                            {selectedStore.telefono && (
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <PhoneIcon color="action" />
                                                    <Typography variant="body2">{selectedStore.telefono}</Typography>
                                                </Stack>
                                            )}
                                            <Divider />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>Distancia</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 900, color: 'primary.main' }}>
                                                    {selectedStore.distancia_km?.toFixed(2)} km
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                startIcon={<NavigationIcon />}
                                                onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${selectedStore.lat},${selectedStore.lon}`)}
                                                sx={{ borderRadius: 2, fontWeight: 800, textTransform: 'none', mt: 1 }}
                                            >
                                                Cómo llegar
                                            </Button>
                                        </Stack>
                                    </CardContent>
                                </MotionCard>
                            )}
                        </AnimatePresence>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}